import { ToastS } from '../../../Service/ToastS';
import { fetchContractById } from '../../../Service/restapi/contractService';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import { DocumentDto } from '../../../Types/DocumentT';
import { EntityReferenceDTO, WorkflowDTO, WorkflowOperation, WorkflowStepDTO } from '../../../Types/LogT';
import { openAcceptOfferModal } from '../Documents/DocumentModals';

const getCurrentWorkflowStep = (workflow: WorkflowDTO): WorkflowStepDTO | undefined =>
  workflow.steps[workflow.currentStep];

const findCreatedEntityId = (workflow: WorkflowDTO, entityReference: EntityReferenceDTO): number | null => {
  const creatingStep = workflow.steps
    .slice(0, workflow.currentStep)
    .reverse()
    .find(
      (step) =>
        step.entityReference?.id === entityReference.id &&
        step.executionReport?.executionStatus === 'COMPLETED' &&
        step.executionReport?.entityId,
    );

  if (!creatingStep?.executionReport?.entityId) {
    return null;
  }

  const entityId = Number(creatingStep.executionReport.entityId);
  return Number.isNaN(entityId) ? null : entityId;
};

export const shouldHandleOfferConfirmationManually = (workflow: WorkflowDTO): boolean => {
  const currentWorkflowStep = getCurrentWorkflowStep(workflow);
  const typeOfInterest = currentWorkflowStep?.executionConfig.typeOfInterest;

  return (
    currentWorkflowStep?.procedureType === 'WAIT_UNTIL' &&
    (typeOfInterest === 'OFFER_CONFIRMED' || typeOfInterest === 'CONTRACT_SIGNED')
  );
};

const getOfferForManualConfirmation = async (
  workflow: WorkflowDTO,
  currentWorkflowStep: WorkflowStepDTO,
  waitEntity: EntityReferenceDTO,
): Promise<DocumentDto | null> => {
  const entityId = findCreatedEntityId(workflow, waitEntity);
  if (!entityId) {
    ToastS.warn('workflow-offer-missing', 'Das zugehoerige Angebot konnte nicht ermittelt werden.');
    return null;
  }

  if (currentWorkflowStep.executionConfig.typeOfInterest === 'OFFER_CONFIRMED') {
    return DocumentS.fetchById(entityId);
  }

  if (currentWorkflowStep.executionConfig.typeOfInterest === 'CONTRACT_SIGNED') {
    const contractResponse = await fetchContractById(entityId);
    if (!contractResponse.ok) {
      ToastS.warn('workflow-contract-missing', 'Der zugehoerige Vertrag konnte nicht ermittelt werden.');
      return null;
    }

    const contract = await contractResponse.json();
    if (!contract.documentId) {
      return null;
    }

    return DocumentS.fetchById(contract.documentId);
  }

  return null;
};

export const handleWorkflowForceContinue = async (
  workflow: WorkflowDTO,
  runOperation: (operation: WorkflowOperation) => void,
) => {
  const currentWorkflowStep = getCurrentWorkflowStep(workflow);
  const waitEntity = currentWorkflowStep?.executionConfig?.entityOfInterest;

  if (!currentWorkflowStep || !shouldHandleOfferConfirmationManually(workflow) || !waitEntity) {
    runOperation('FORCE_CONTINUE');
    return;
  }

  const offer: any = await getOfferForManualConfirmation(workflow, currentWorkflowStep, waitEntity);
  if (!offer) {
    runOperation('FORCE_CONTINUE');
    return;
  }

  const hasOptionalOfferPositions =
    offer.documentType === 'OFFER' && Array.isArray(offer.positions) && offer.positions.some((position: any) => position.optional);

  if (hasOptionalOfferPositions) {
    openAcceptOfferModal({
      bookitupDocument: offer,
      onAccept: () => runOperation('FORCE_CONTINUE'),
      source: currentWorkflowStep.executionConfig.typeOfInterest === 'CONTRACT_SIGNED' ? 'contract' : 'offer',
    });
    return;
  }

  runOperation('FORCE_CONTINUE');
};
